import axios from 'axios';
import Cookies from 'js-cookie';
import { BACKEND_API_URL } from '../utils/api';

const getAuthHeaders = () => ({
    Authorization: `Token ${Cookies.get('access_token')}`,
    'Content-Type': 'application/json',
});

/**
 * Fetch all channels (including archived)
 * @param {Object} options - Fetch options
 * @param {boolean} options.sortOwnFirst - Sort user's own personalized channel first (default: false)
 * @returns {Promise<Array>} Array of channels
 */
export const fetchChannels = async ({ sortOwnFirst = false } = {}) => {
    const params = { include_archived: 'true' };
    if (sortOwnFirst) {
        params.sort_own_first = 'true';
    }
    const response = await axios.get(`${BACKEND_API_URL}/api/channels/`, {
        headers: getAuthHeaders(),
        params,
    });
    return Array.isArray(response.data) ? response.data : [];
};

/**
 * Fetch messages for a specific channel
 */
export const fetchMessages = async (channelId) => {
    const response = await axios.get(`${BACKEND_API_URL}/api/channels/${channelId}/messages/`, {
        headers: getAuthHeaders(),
    });
    return response.data;
};

/**
 * Update last viewed channel
 */
export const updateLastViewedChannel = async (channelId) => {
    await axios.post(
        `${BACKEND_API_URL}/api/channels/${channelId}/update_last_viewed/`,
        {},
        { headers: getAuthHeaders() }
    );
};

/**
 * Fetch a single message by ID (for thread loading)
 */
export const fetchMessage = async (messageId) => {
    const response = await axios.get(`${BACKEND_API_URL}/api/message/${messageId}/`, {
        headers: getAuthHeaders(),
    });
    return response.data;
};

/**
 * Archive/unarchive a channel
 */
export const toggleChannelArchive = async (channelId, isArchived) => {
    const response = await axios.patch(
        `${BACKEND_API_URL}/api/channels/${channelId}/`,
        { is_archived: isArchived },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Search channels with fuzzy matching
 * Supports request cancellation via cancel token
 * 
 * @param {string} query - Search query string
 * @param {boolean} includeArchived - Whether to include archived channels
 * @param {object} cancelTokenSource - Axios CancelToken source for request cancellation
 * @returns {Promise<Array>} Array of matching channels (max 10)
 */
export const searchChannels = async (query, includeArchived = true, cancelTokenSource = null) => {
    if (!query || !query.trim()) {
        return [];
    }

    const params = {
        search: query.trim(),
        include_archived: includeArchived ? 'true' : 'false',
    };

    const config = {
        headers: getAuthHeaders(),
        params,
    };

    if (cancelTokenSource && cancelTokenSource.token) {
        config.cancelToken = cancelTokenSource.token;
    }

    try {
        const response = await axios.get(`${BACKEND_API_URL}/api/channels/`, config);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        if (axios.isCancel(error)) {
            return [];
        }
        throw error;
    }
};

/**
 * Fetch all channel groups
 * @returns {Promise<Array>} Array of channel groups
 */
export const fetchChannelGroups = async () => {
    const response = await axios.get(`${BACKEND_API_URL}/api/channel-groups/`, {
        headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
};

